import type {
  Effect,
  GameState,
  LaneIndex,
  PlayerId,
  Side,
  TableId,
} from '@cards/engine';
import {
  LANE_INDEXES,
  SIDES,
  compareCellIds,
  parseCellId,
} from '@cards/engine';
import { getCardUi, type PublicCardFact, type PublicCardRelation } from '@cards/cards';
import { projectPublicFacts } from './cardUiAdapter.js';

export interface PublicLocationEffectScopeView {
  readonly kind: 'table' | 'lane' | 'cell';
  readonly label: string;
  readonly lane?: LaneIndex;
  readonly side?: PublicCardRelation;
}

/**
 * Id-less projection for one public board effect. `key` only stabilizes the
 * current rendered list; it is not an authoritative EffectId or sequence.
 */
export interface PublicLocationEffectView {
  readonly key: string;
  readonly definitionId: string;
  readonly scope: PublicLocationEffectScopeView;
  readonly duration?: number;
  readonly persistent: boolean;
  readonly facts: Readonly<Record<string, PublicCardFact>>;
  readonly presentation?: Readonly<{
    readonly symbol: string;
    readonly label: string;
    readonly description: string;
    readonly counter?: number;
    readonly showCellBadge: boolean;
  }>;
}

export interface PublicCellEffectBadgeView {
  readonly key: string;
  /** Public effect-card definition id — lets cell previews render the card. */
  readonly definitionId: string;
  readonly symbol: string;
  readonly label: string;
  readonly description: string;
  readonly value: number;
}

export interface PublicLocationEffectGroupView {
  readonly key: string;
  readonly kind: 'table' | 'lane' | 'cell';
  readonly label: string;
  readonly lane?: LaneIndex;
  readonly side?: PublicCardRelation;
  readonly effects: readonly PublicLocationEffectView[];
}

export interface PublicLocationEffectsView {
  readonly table: TableId;
  readonly label: string;
  readonly effects: readonly PublicLocationEffectView[];
  readonly groups: readonly PublicLocationEffectGroupView[];
}

function relation(side: Side, viewerSide: Side): PublicCardRelation {
  return side === viewerSide ? 'viewer' : 'opponent';
}

function sideLabel(side: PublicCardRelation): string {
  return side === 'viewer' ? 'Ваша сторона' : 'Сторона соперника';
}

function inTable(effect: Effect, table: TableId): boolean {
  if (effect.scope.kind === 'table' || effect.scope.kind === 'lane') {
    return effect.scope.tableId === table;
  }
  return effect.scope.kind === 'cell' && parseCellId(effect.scope.cellId).tableId === table;
}

function scopeRank(effect: Effect): number {
  return effect.scope.kind === 'table' ? 0 : effect.scope.kind === 'lane' ? 1 : 2;
}

function compareLocationEffects(a: Effect, b: Effect): number {
  const rank = scopeRank(a) - scopeRank(b);
  if (rank !== 0) return rank;
  if (a.scope.kind === 'lane' && b.scope.kind === 'lane') {
    return a.scope.laneIndex - b.scope.laneIndex || a.seq - b.seq;
  }
  if (a.scope.kind === 'cell' && b.scope.kind === 'cell') {
    return compareCellIds(a.scope.cellId, b.scope.cellId) || a.seq - b.seq;
  }
  return a.seq - b.seq;
}

function projectScope(effect: Effect, table: TableId, viewerSide: Side): PublicLocationEffectScopeView {
  const scope = effect.scope;
  if (scope.kind === 'table') {
    return Object.freeze({ kind: 'table', label: `Локация ${table + 1}` });
  }
  if (scope.kind === 'lane') {
    return Object.freeze({
      kind: 'lane',
      lane: scope.laneIndex,
      label: `Линия ${scope.laneIndex + 1}`,
    });
  }
  if (scope.kind !== 'cell') throw new Error('projectLocationEffects: non-location scope');
  const cell = parseCellId(scope.cellId);
  const projectedSide = relation(cell.side, viewerSide);
  return Object.freeze({
    kind: 'cell',
    lane: cell.laneIndex,
    side: projectedSide,
    label: `${sideLabel(projectedSide)} · линия ${cell.laneIndex + 1}`,
  });
}

function groupKey(
  table: TableId,
  kind: PublicLocationEffectGroupView['kind'],
  lane?: LaneIndex,
  side?: PublicCardRelation,
): string {
  return `location-${table}-${kind}-${lane ?? 'all'}-${side ?? 'both'}`;
}

/**
 * The input must already be the viewer's redacted snapshot. The returned DTO
 * strips effect/source/instance ids, raw vars and numeric seq values.
 */
export function projectLocationEffectsFromRedactedSnapshot(
  snapshot: GameState,
  viewerId: PlayerId,
  table: TableId,
): PublicLocationEffectsView {
  const viewer = snapshot.players[viewerId];
  if (viewer === undefined) throw new Error(`projectLocationEffects: unknown viewer "${viewerId}"`);

  const sorted = snapshot.effects.filter((effect) => inTable(effect, table)).sort(compareLocationEffects);
  const effects = Object.freeze(sorted.map((effect, index): PublicLocationEffectView => {
    const ui = getCardUi(effect.defId);
    const facts = projectPublicFacts(effect.vars, ui);
    const locationEffect = ui?.locationEffect;
    const counter = locationEffect?.counterFact === undefined
      ? undefined
      : facts[locationEffect.counterFact];
    return Object.freeze({
      key: `location-${table}-effect-${index}`,
      definitionId: effect.defId,
      scope: projectScope(effect, table, viewer.side),
      ...(effect.duration === undefined ? {} : { duration: effect.duration }),
      persistent: effect.persistent === true,
      facts,
      ...(locationEffect === undefined ? {} : {
        presentation: Object.freeze({
          symbol: locationEffect.symbol,
          label: locationEffect.label,
          description: locationEffect.description,
          ...(typeof counter === 'number' ? { counter } : {}),
          showCellBadge: locationEffect.showCellBadge === true,
        }),
      }),
    });
  }));

  const groups: PublicLocationEffectGroupView[] = [];
  const appendGroup = (
    kind: PublicLocationEffectGroupView['kind'],
    label: string,
    lane?: LaneIndex,
    side?: PublicCardRelation,
  ): void => {
    groups.push(Object.freeze({
      key: groupKey(table, kind, lane, side),
      kind,
      label,
      ...(lane === undefined ? {} : { lane }),
      ...(side === undefined ? {} : { side }),
      effects: Object.freeze(effects.filter((effect) => effect.scope.kind === kind
        && (lane === undefined || effect.scope.lane === lane)
        && (side === undefined || effect.scope.side === side))),
    }));
  };

  appendGroup('table', `Локация ${table + 1}`);
  for (const lane of LANE_INDEXES) appendGroup('lane', `Линия ${lane + 1}`, lane);
  // Preserve engine canonical cell order: lane ASC, side A before B. The DTO
  // exposes only viewer-relative sides, never raw side/player identifiers.
  for (const lane of LANE_INDEXES) {
    for (const side of SIDES) {
      const projectedSide = relation(side, viewer.side);
      appendGroup('cell', `${sideLabel(projectedSide)} · линия ${lane + 1}`, lane, projectedSide);
    }
  }

  return Object.freeze({
    table,
    label: `Локация ${table + 1}`,
    effects,
    groups: Object.freeze(groups),
  });
}

export function cellEffects(
  location: PublicLocationEffectsView,
  lane: LaneIndex,
  side: PublicCardRelation,
): readonly PublicLocationEffectView[] {
  return location.groups.find((group) =>
    group.kind === 'cell' && group.lane === lane && group.side === side)?.effects ?? [];
}

export function locationEffectBadges(
  effects: readonly PublicLocationEffectView[],
): readonly PublicCellEffectBadgeView[] {
  return Object.freeze(effects.flatMap((effect): PublicCellEffectBadgeView[] => {
    const presentation = effect.presentation;
    if (presentation?.showCellBadge !== true || presentation.counter === undefined
      || !Number.isFinite(presentation.counter) || presentation.counter <= 0) return [];
    return [Object.freeze({
      key: `${effect.key}-badge`,
      definitionId: effect.definitionId,
      symbol: presentation.symbol,
      label: presentation.label,
      description: presentation.description.replaceAll('{value}', String(presentation.counter)),
      value: presentation.counter,
    })];
  }));
}
